import axios from "axios";

/*
 * The spare parts register and its stock ledger.
 *
 * Both live under /api/spare-parts: movements are a sub-resource of the part they belong to,
 * which is why there is no separate inventory endpoint to keep in step.
 *
 * Nothing here sends a user id. The server takes "who did this" from the token
 * and overwrites anything a request supplied, so passing one would only suggest it mattered.
 *
 * Shaped like cmmsRecurringSchedule.jsx, including throwResponseData — the CMMS controllers
 * answer a failure with ApiErrorResponse { code, message }, and rethrowing the body rather than
 * the axios error is what lets formatCmmsError show the server's own sentence instead of
 * "Request failed with status code 400".
 */
const baseUrl = "/api/spare-parts";

const throwResponseData = (error) => {
  throw error.response?.data ?? error;
};

export const getSpareParts = async (companyKey, includeInactive = false) => {
  try {
    const response = await axios.get(baseUrl, { params: { companyKey, includeInactive } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getSparePartById = async (companyKey, id) => {
  try {
    const response = await axios.get(`${baseUrl}/${id}`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createSparePart = async (sparePart) => {
  try {
    const response = await axios.post(baseUrl, sparePart);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const updateSparePart = async (id, sparePart) => {
  try {
    const response = await axios.put(`${baseUrl}/${id}`, sparePart);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Retire, not delete. There is no DELETE endpoint on purpose: consumption rows name these
 * records, and a retired part number stays taken so it cannot silently be reused for a
 * different physical thing.
 */
export const deactivateSparePart = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/deactivate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * The way back out of a retire.
 *
 * Not just an undo for a mis-click: stock left on a part when it was retired is unreachable
 * until it is active again, because movements are refused against an inactive part. So this is
 * also the only route to writing that balance off.
 */
export const activateSparePart = async (companyKey, id) => {
  try {
    const response = await axios.post(`${baseUrl}/${id}/activate`, null, {
      params: { companyKey },
    });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const getStockMovements = async (companyKey, sparePartId) => {
  try {
    const response = await axios.get(`${baseUrl}/${sparePartId}/movements`, { params: { companyKey } });
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

export const createStockMovement = async (sparePartId, movement) => {
  try {
    const response = await axios.post(`${baseUrl}/${sparePartId}/movements`, movement);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};

/*
 * Files a physical count. The body carries countedQuantity — what is on the shelf — never a
 * delta: the difference is worked out server-side against the balance as it stands when the
 * count lands. Answers { sparePartId, quantityOnHand }.
 */
export const stocktakeSparePart = async (sparePartId, request) => {
  try {
    const response = await axios.post(`${baseUrl}/${sparePartId}/stocktake`, request);
    return response.data;
  } catch (error) {
    throwResponseData(error);
  }
};
